import { Injectable } from '@angular/core';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { overlayHtmlSchema, overlayPlacementSchema, overlayTextSchema } from '@shared/ragai.schemas';
import { Subject } from 'rxjs';
import { z } from 'zod';
import { DOMManagerService } from './dommanager.service';
import { EventManagerService } from './event-manager/event-manager.service';
import { StageManagerService } from './stage-manager.service';

/**
 * Zeigt modale Overlays (Text, Buttons, HTML, Datei-Upload, Ladeanimation) über dem Chat an (`RAGAI.OVERLAY`).
 * @remarks
 * Im Gegensatz zu `RAGAI.CARDS` sind Overlays **nicht** Teil des persistenten Chatverlaufs - sie legen sich
 * modal über die aktuelle Ansicht und verschwinden wieder, ohne im Verlauf gespeichert zu werden. Pro Zeitpunkt
 * existiert höchstens ein Overlay: Jede neue `show*`-Methode schließt implizit ein zuvor offenes Overlay.
 * {@link setPlacement} bestimmt, in welchem Dokument (eigenes Bot-Fenster oder das umgebende Elternfenster bei
 * `'fusionview'`) das Overlay eingehängt wird - wichtig, wenn der Bot in eine Fusion-View (LMS-Kursseite)
 * eingebettet läuft und das Overlay die gesamte Seite abdecken soll statt nur das (oft kleine) Bot-Iframe.
 */
@Injectable({
  providedIn: 'root'
})

export class OverlayManagerService {
  public isOpen = false;
  public onClosed = new Subject<void>();
  private placement: 'botview' | 'fusionview' = 'botview';
  constructor(
    private stageManager: StageManagerService,
    private eventManager: EventManagerService,
    private dom: DOMManagerService,
    private toastr: ToastrService
  ) { }

  /** Schließt das aktuell angezeigte Overlay (im Bot- und ggf. Fusion-View-Fenster). */
  public close() {
    this.isOpen = false;
    this.onClosed.next();
    /** NORMAL IM CHAT */
    const existingOverlay = document.getElementById('custom-overlay');
    const overlayClass = Array.from(existingOverlay?.classList ?? []).find(cls => cls.startsWith('overlay-'));
    if (existingOverlay) {
      this.eventManager.trigger("RAGAI.OVERLAY.CLOSE", { type: overlayClass });
      existingOverlay.remove();
    }
    try {
      if (parent === window) return;
      if (parent.document === document) return;
      const existingOverlayFusion = parent.document.getElementById('custom-overlay');
      const overlayClassFusion = Array.from(existingOverlay?.classList ?? []).find(cls => cls.startsWith('overlay-'));
      if (existingOverlayFusion) {
        this.eventManager.trigger("RAGAI.OVERLAY.CLOSE", { type: overlayClassFusion });
        existingOverlayFusion.remove();
      }
    } catch (error) {
      console.warn("Error while closing overlay. Permissions on bot iframe may not sufficient: ", error);
    }
  }

  /**
   * Legt fest, in welchem Fenster (Bot- oder Fusion-View) Overlays angezeigt werden.
   * @param placement Zielfenster, oder ein Objekt gemäß `overlayPlacementSchema`.
   */
  public setPlacement(placement: 'botview' | 'fusionview' | z.input<typeof overlayPlacementSchema>): void {
    const parsed = overlayPlacementSchema.safeParse(placement);
    if (parsed.success && typeof placement === 'object') {
      placement = parsed.data.placement;
    }
    this.placement = placement as 'botview' | 'fusionview';
  };

  private _appendOverlay(overlay: HTMLElement) {
    if (this.placement === 'botview') {
      document.body.appendChild(overlay);
    }
    if (this.placement === 'fusionview') {
      parent.document.body.appendChild(overlay);
    }
  }

  /**
   * Liefert das Document, in dem das aktuelle Overlay tatsächlich hängt (wichtig bei placement 'fusionview').
   * @returns Das Eltern-Dokument bei `'fusionview'`, sonst das eigene Dokument.
   */
  private getOverlayDocument(): Document {
    if (this.placement === 'fusionview') {
      try {
        if (parent !== window && parent.document !== document) {
          return parent.document;
        }
      } catch (error) {
        console.warn("Error while accessing parent document. Permissions on bot iframe may not be sufficient: ", error);
      }
    }
    return document;
  }

  /**
   * Zeigt ein Text-Overlay an.
   * @param text Der anzuzeigende Text, oder ein Objekt gemäß `overlayTextSchema`.
   * @param onClose Callback, der beim Schließen des Overlays aufgerufen wird.
   */
  public text(text: string | z.input<typeof overlayTextSchema>, onClose?: () => void): void {
    const parsed = overlayTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    this.close();
    const overlay = document.createElement('div');
    overlay.classList.add('overlay-text');
    overlay.id = 'custom-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;align-items:center;justify-content:center;z-index:2000;font-size:1.5em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:left;max-width:80%;max-height:80%;overflow:auto;';
    const closeButton = document.createElement('span');
    closeButton.id = 'custom-overlay-close';
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:2001;';
    closeButton.onclick = () => {
      overlay.remove();
      if (onClose) onClose();
    };
    const textElement = document.createElement('p');
    textElement.style.cssText = 'white-space:pre-wrap;text-align:left;';
    textElement.innerHTML = (text as string).replace(/\n/g, '<br>');
    content.appendChild(textElement);
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.OVERLAY.OPEN.text", { overlay: overlay });
  }

  /**
   * Zeigt ein Overlay mit Text und Buttons an.
   * @param text Der anzuzeigende Text.
   * @param buttons Die anzuzeigenden Buttons.
   * @param onClose Callback, der beim Schließen des Overlays aufgerufen wird.
   * @param cssClass Optionale CSS-Klasse für das Overlay.
   */
  public textAndButton(text: string, buttons: { text: string, onClick: () => void, className?: string }[], onClose?: () => void, cssClass = '') {
    this.close();
    text = String(text);
    const overlay = document.createElement('div');
    overlay.classList.add('overlay-text-and-buttons');
    if (cssClass) overlay.classList.add(cssClass);
    overlay.id = 'custom-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;align-items:center;justify-content:center;z-index:1000;font-size:1.5em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:center;max-width:80%;max-height:80%;overflow:auto;';
    const closeButton = document.createElement('span');
    closeButton.id = 'custom-overlay-close';
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:1001;';
    closeButton.onclick = () => {
      this.close();
      if (onClose) onClose();
    };
    const textElement = document.createElement('p');
    textElement.style.cssText = 'white-space:pre-wrap;text-align:left;margin-bottom:20px;';
    textElement.innerHTML = text.replace(/\n/g, '<br>');
    content.appendChild(textElement);
    buttons.forEach(buttonData => {
      const button = document.createElement('button');
      button.innerText = buttonData.text;
      button.style.cssText = 'margin:10px;padding:10px 20px';
      button.className = 'btn btn-primary';
      if (buttonData.className) {
        button.className += ' ' + buttonData.className;
      }
      button.onclick = buttonData.onClick;
      content.appendChild(button);
    });
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.OVERLAY.OPEN.textAndButtons", { overlay: overlay, text, buttons });
  }

  /**
   * Zeigt ein Overlay mit Texteingabefeld und Buttons an.
   * @param text Der anzuzeigende Hinweistext.
   * @param buttons Die anzuzeigenden Buttons.
   * @param onClose Callback, der beim Schließen des Overlays aufgerufen wird.
   * @param cssClass Optionale CSS-Klasse für das Overlay.
   */
  public prompt(text: string = "", buttons: { text: string, onClick: () => void, className?: string }[] = [], onClose?: () => void, cssClass = '') {
    this.close();
    text = String(text);
    const overlay = document.createElement('div');
    overlay.id = 'custom-overlay';
    overlay.classList.add('overlay-prompt');
    if (cssClass) overlay.classList.add(cssClass);
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;align-items:center;justify-content:center;z-index:1000;font-size:1.5em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:center;max-width:80%;max-height:80%;overflow:auto;';
    const closeButton = document.createElement('span');
    closeButton.id = 'custom-overlay-close';
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:1001;';
    closeButton.onclick = () => {
      overlay.remove();
      if (onClose) onClose();
    };
    const textElement = document.createElement('p');
    textElement.style.cssText = 'white-space:pre-wrap;text-align:left;margin-bottom:20px;';
    textElement.innerHTML = text.replace(/\n/g, '<br>');
    const inputElement = this.dom.createElementFromHTML('<input id="overlay-prompt-input" type="text" class="form-control "form-control-overlay-prompt">');
    content.appendChild(textElement);
    content.appendChild(inputElement);
    buttons.forEach(buttonData => {
      const button = document.createElement('button');
      button.innerText = buttonData.text;
      button.style.cssText = 'margin:10px;padding:10px 20px';
      button.className = 'btn btn-primary';
      if (buttonData.className) {
        button.className += ' ' + buttonData.className;
      }
      button.onclick = buttonData.onClick;
      content.appendChild(button);
    });
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.OVERLAY.OPEN.prompt", { text, buttons });
  }

  /**
   * Zeigt ein Lade-Overlay mit Spinner an.
   * @param text Der anzuzeigende Ladetext, oder ein Objekt gemäß `overlayTextSchema`.
   * @param cssClass Optionale CSS-Klasse für das Overlay.
   */
  public loading(text: string | z.input<typeof overlayTextSchema> = "Wird geladen ...", cssClass = ''): void {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    text = String(text);
    const parsed = overlayTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    this.close();
    const overlay = document.createElement('div');
    overlay.classList.add('overlay-loading');
    if (cssClass) overlay.classList.add(cssClass);
    overlay.id = 'custom-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:1000;font-size:1.5em;padding:20px;box-sizing:border-box;';
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      border: 8px solid rgba(255, 255, 255, 0.2);
      border-top: 8px solid white;
      border-radius: 50%;
      width: 60px;
      height: 60px;
      animation: spin 1s linear infinite;
      margin-bottom: 20px;
    `;
    function appendSpinnerAnimation(doc: Document) {
      if (!doc.getElementById('custom-overlay-spinner-style')) {
        const style = doc.createElement('style');
        style.id = 'custom-overlay-spinner-style';
        style.innerHTML = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
        doc.head.appendChild(style);
      }
    }
    if (this.placement === 'botview') {
      appendSpinnerAnimation(document);
    } else {
      appendSpinnerAnimation(parent.document);
    }
    // Füge den Spinner-Animationsstil zum Dokument hinzu (nur einmal)
    if (!document.getElementById('custom-overlay-spinner-style')) {
      const style = document.createElement('style');
      style.id = 'custom-overlay-spinner-style';
      style.innerHTML = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
    const textElement = document.createElement('div');
    textElement.innerText = text;
    textElement.style.textAlign = 'center';
    overlay.appendChild(spinner);
    overlay.appendChild(textElement);
    this._appendOverlay(overlay);
    this.isOpen = true;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.OVERLAY.OPEN.loading", { overlay, text });
  }

  /**
   * Zeigt ein Overlay mit mehrzeiligem Texteingabefeld (Textarea) und Buttons an.
   * @param text Der anzuzeigende Hinweistext.
   * @param buttons Die anzuzeigenden Buttons.
   * @param onClose Callback, der beim Schließen des Overlays aufgerufen wird.
   * @param cssClass Optionale CSS-Klasse für das Overlay.
   */
  public promptArea(text: string = "", buttons: { text: string, onClick: () => void, className?: string }[] = [], onClose?: () => void, cssClass = '') {
    text = String(text);
    this.close();
    const overlay = document.createElement('div');
    overlay.id = 'custom-overlay';
    if (cssClass) overlay.classList.add(cssClass);
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;align-items:center;justify-content:center;z-index:1000;font-size:1.5em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:center;max-width:80%;max-height:80%;overflow:auto;';
    const closeButton = document.createElement('span');
    closeButton.id = 'custom-overlay-close';
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:1001;';
    closeButton.onclick = () => {
      overlay.remove();
      if (onClose) onClose();
    };
    const textElement = document.createElement('p');
    textElement.style.cssText = 'white-space:pre-wrap;text-align:left;margin-bottom:20px;';
    textElement.innerHTML = text.replace(/\n/g, '<br>');
    const inputElement = this.dom.createElementFromHTML('<textarea id="overlay-prompt-input" type="text" class="form-control "form-control-overlay-prompt"></textarea>');
    content.appendChild(textElement);
    content.appendChild(inputElement);
    buttons.forEach(buttonData => {
      const button = document.createElement('button');
      button.innerText = buttonData.text;
      button.style.cssText = 'margin:10px;padding:10px 20px';
      button.className = 'btn btn-primary';
      if (buttonData.className) {
        button.className += ' ' + buttonData.className;
      }
      button.onclick = buttonData.onClick;
      content.appendChild(button);
    });
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
  }

  /**
   * Gibt das Eingabefeld eines aktuell offenen `prompt()`-Overlays zurück.
   * @returns Das Eingabefeld-Element, oder `null`.
   */
  public prompt_getInput(): HTMLInputElement | null {
    return this.getOverlayDocument().getElementById("overlay-prompt-input") as HTMLInputElement | null;
  }

  /**
   * Gibt das Textarea-Element eines aktuell offenen `promptArea()`-Overlays zurück.
   * @returns Das Textarea-Element, oder `null`.
   */
  public fileUpload_getTextArea(): HTMLInputElement | null {
    return this.getOverlayDocument().getElementById("fileupload-textarea") as HTMLInputElement | null;
  }

  /**
   * Gibt den aktuellen Wert des `promptArea()`-Textarea-Elements zurück.
   * @returns Der eingegebene Text, oder ein leerer String.
   */
  public fileUpload_getValue(): string {
    const selektor = this.fileUpload_getTextArea();
    return selektor ? selektor.value : "";
  }

  /**
   * Gibt den aktuellen Wert des `prompt()`-Eingabefelds zurück.
   * @returns Der eingegebene Text, oder ein leerer String.
   */
  public prompt_getInputValue(): string {
    const selektor = this.prompt_getInput();
    return selektor ? selektor.value : "";
  }

  /**
   * Zeigt ein Overlay ausschließlich mit Buttons an (ohne Text).
   * @param buttons Die anzuzeigenden Buttons.
   * @param onClose Callback, der beim Schließen des Overlays aufgerufen wird.
   * @param cssClass Optionale CSS-Klasse für das Overlay.
   */
  public button(buttons: { text: string, onClick: () => void, className?: string }[], onClose?: () => void, cssClass = '') {
    const existingOverlay = document.getElementById('custom-overlay');
    if (existingOverlay) existingOverlay.remove();
    const overlay = document.createElement('div');
    overlay.id = 'custom-overlay';
    overlay.classList.add('overlay-button');
    if (cssClass) overlay.classList.add(cssClass);
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;align-items:center;justify-content:center;z-index:1000;font-size:1.5em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:center;max-width:80%;max-height:80%;overflow:auto;';
    const closeButton = document.createElement('span');
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:1001;';
    closeButton.onclick = () => {
      this.close();
      if (onClose) onClose();
    };
    buttons.forEach(buttonData => {
      const button = document.createElement('button');
      button.innerText = buttonData.text;
      button.style.cssText = 'margin:10px;padding:10px 20px';
      button.className = 'btn btn-primary';
      if (buttonData.className) {
        button.className += ' ' + buttonData.className;
      }
      button.onclick = buttonData.onClick;
      content.appendChild(button);
    });
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
  }

  /**
   * Zeigt ein HTML-Overlay an.
   * @param html Der anzuzeigende HTML-Inhalt, oder ein Objekt gemäß `overlayHtmlSchema`.
   * @param onClose Callback, der beim Schließen des Overlays aufgerufen wird.
   * @param cssClass Optionale CSS-Klasse für das Overlay.
   */
  public html(html: string | z.input<typeof overlayHtmlSchema>, onClose?: () => void, cssClass = ''): void {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    html = String(html);
    const parsed = overlayHtmlSchema.safeParse(html);
    if (parsed.success && typeof html === 'object') {
      html = parsed.data.html;
    }
    this.close();
    const overlay = document.createElement('div');
    overlay.id = 'custom-overlay';
    if (cssClass) overlay.classList.add(cssClass);
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;z-index:1000;font-size:1.5em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:center;max-width:80%;max-height:80%;overflow:auto;';
    const closeButton = document.createElement('span');
    closeButton.id = 'custom-overlay-close';
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:1001;';
    closeButton.onclick = () => {
      overlay.remove();
      if (onClose) onClose();
    };
    const htmlElement = document.createElement('div');
    htmlElement.style.cssText = 'white-space:normal;text-align:left;margin-bottom:20px;';
    htmlElement.innerHTML = html;
    content.appendChild(htmlElement);
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
    // parent.document.body.appendChild(overlay);
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.OVERLAY.OPEN.html", { overlay, html });
  }

  /**
   * Zeigt ein Overlay mit Buttons zum Wechseln in eine andere sichtbare Stage.
   * @param text Optionaler Hinweistext.
   */
  public stageChangeDialog(text: string = "") {
    const data: { text: string, onClick: () => void, className?: string }[] = [];
    for (const stage of this.stageManager.visibleStages) {
      if (this.stageManager.currentStage && this.stageManager.currentStage.name !== stage.name) {
        data.push({
          text: stage.title, onClick: async () => {
            await this.stageManager.goTo(stage.name);
            this.close();
          }
        });
      }
    }
    if (text !== "") {
      this.textAndButton(text, data);
    } else {
      this.button(data);
    }
  }


  /**
   * Zeigt ein Overlay zum Hochladen eines Bildes per Drag-and-Drop an.
   * @param title Titel des Overlays.
   * @param instruction Hinweistext in der Drop-Zone.
   * @param onImageLoaded Callback mit dem Bildinhalt (Data-URL), sobald ein Bild geladen wurde.
   * @param onClose Callback beim Schließen des Overlays.
   */
  public uploadImage(title: string = "Bild hochladen", instruction: string = "Bild hierher ziehen (PNG, JPEG, WEBP, Non-animated GIF)", onImageLoaded?: (fileContent: string, fileName?: string, fileType?: string) => void, onClose?: () => void) {
    this.close();
    const overlay = document.createElement('div');
    overlay.id = 'custom-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;align-items:center;justify-content:center;z-index:1000;font-size:1.2em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:center;max-width:80%;max-height:80%;overflow:auto;background:#222;padding:20px;border-radius:8px;';
    const closeButton = document.createElement('span');
    closeButton.id = 'custom-overlay-close';
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:1001;';
    closeButton.onclick = () => {
      this.close();
      if (onClose) onClose();
    };
    const heading = document.createElement('h2');
    heading.innerText = title;
    heading.style.marginBottom = '20px';
    content.appendChild(heading);
    const dropZone = document.createElement('div');
    dropZone.style.cssText = 'border:2px dashed white;padding:40px;margin-bottom:20px;border-radius:8px;';
    dropZone.innerText = instruction;
    dropZone.ondragover = (e) => {
      e.preventDefault();
      dropZone.style.backgroundColor = '#333';
    };
    dropZone.ondragleave = () => {
      dropZone.style.backgroundColor = '';
    };
    dropZone.ondrop = (e) => {
      e.preventDefault();
      dropZone.style.backgroundColor = '';
      const file = e.dataTransfer?.files[0];
      const fileName = file?.name;
      const fileType = file?.type;
      if (!file) return;
      if (['image/png', 'image/jpeg', 'image/gif', 'image/webp'].findIndex(type => type == file.type) == -1) {
        this.toastr.error('ERROR', $localize`:@@image-format-error:Dieses Bildformat wird nicht unterstützt. Erlaubt sind: PNG, JPEG, WEBP und nicht-animierte GIF.`, { timeOut: 10000 });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const fileContent = reader.result as string;
        image.src = fileContent;
        if (onImageLoaded) onImageLoaded(fileContent, fileName, fileType);
      };
      reader.readAsDataURL(file);
    };
    const image = document.createElement('img');
    image.id = 'fileupload-img';
    content.appendChild(dropZone);
    content.appendChild(image);
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.OVERLAY.OPEN.uploadImage", { overlay, title, instruction });
  }

  /**
   * Zeigt ein Overlay zum Hochladen einer Textdatei per Drag-and-Drop an.
   * @param title Titel des Overlays.
   * @param instruction Hinweistext in der Drop-Zone.
   * @param onTextLoaded Callback mit dem Dateiinhalt, sobald eine Datei geladen wurde.
   * @param onClose Callback beim Schließen des Overlays.
   */
  public uploadFile(title: string = "Textdatei hochladen", instruction: string = 'Datei hierher ziehen (.txt oder textbasiert)', onTextLoaded?: (fileContent: string, fileName?: string, fileType?: string) => void, onClose?: () => void) {
    title = title + "";
    this.close();
    const overlay = document.createElement('div');
    overlay.id = 'custom-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.8);color:white;display:flex;align-items:center;justify-content:center;z-index:1000;font-size:1.2em;padding:20px;box-sizing:border-box;';
    const content = document.createElement('div');
    content.style.cssText = 'position:relative;text-align:center;max-width:80%;max-height:80%;overflow:auto;background:#222;padding:20px;border-radius:8px;';
    const closeButton = document.createElement('span');
    closeButton.id = 'custom-overlay-close';
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = 'position:fixed;top:20px;right:20px;font-size:2em;font-weight:bold;color:white;cursor:pointer;z-index:1001;';
    closeButton.onclick = () => {
      overlay.remove();
      if (onClose) onClose();
    };
    const heading = document.createElement('h2');
    heading.innerText = title;
    heading.style.marginBottom = '20px';
    content.appendChild(heading);
    const dropZone = document.createElement('div');
    dropZone.style.cssText = 'border:2px dashed white;padding:40px;margin-bottom:20px;border-radius:8px;';
    dropZone.innerText = instruction;
    dropZone.ondragover = (e) => {
      e.preventDefault();
      dropZone.style.backgroundColor = '#333';
    };
    dropZone.ondragleave = () => {
      dropZone.style.backgroundColor = '';
    };
    dropZone.ondrop = (e) => {
      e.preventDefault();
      dropZone.style.backgroundColor = '';
      const file = e.dataTransfer?.files[0];
      const fileName = file?.name;
      const fileType = file?.type;
      if (!file) return;
      if (!file.type.startsWith('text/')) {
        this.toastr.error('ERROR', $localize`:@@file-format-error:Dieses Dateiformat wird nicht unterstützt. Bitte verwende eine Textdatei.`, { timeOut: 10000 });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const fileContent = reader.result as string;
        textarea.value = fileContent;
        if (onTextLoaded) onTextLoaded(fileContent, fileName, fileType);
      };
      reader.readAsText(file);
    };
    const textarea = document.createElement('textarea');
    textarea.style.cssText = 'width:100%;height:200px;padding:10px;font-family:monospace;display:none';
    textarea.placeholder = 'Inhalt der Textdatei wird hier angezeigt...';
    textarea.id = 'fileupload-textarea';
    content.appendChild(dropZone);
    content.appendChild(textarea);
    overlay.appendChild(closeButton);
    overlay.appendChild(content);
    this._appendOverlay(overlay);
    this.isOpen = true;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.OVERLAY.OPEN.uploadFile", { overlay, title, instruction });
  }

  /**
   * Zeigt ein Overlay mit Buttons zu allen sichtbaren Stages an.
   * @param text Optionaler Hinweistext.
   * @param cssClass Optionale CSS-Klasse für das Overlay.
   */
  public stageList(text: string = "", cssClass = '') {
    const data: { text: string, onClick: () => void, className?: string }[] = [];
    for (const stage of this.stageManager.visibleStages) {
      data.push({
        text: stage.title, onClick: async () => {
          await this.stageManager.goTo(stage.name);
          this.close();
        }
      });
    }
    if (text !== "") {
      this.textAndButton(text, data, undefined, cssClass);
    } else {
      this.button(data, undefined, cssClass);
    }
  }
}
